/* DreamCloud → Needs attention: the worker's open problems, fatal first.
 *
 * Each row's button opens what is behind the problem, inline under the row:
 * the failures recorded under its signature, the online bookings that arrived
 * without a click, or a job's run history. Unsent sales go to the replay
 * dialog instead, which lives in the Actions section, so that one is passed up
 * as a `replay` event with the dates the worker recorded.
 *
 * Which button does what, the order and the staleness wording are all in
 * js/rules/dreamcloud.js; this file renders them.
 */
import * as Vue from 'vue';
import { dreamcloud } from '../dreamcloud.js';
import { orderProblems, problemAction, staleness, formatWhen } from '../rules/dreamcloud.js';
import { useDrilldown, DcRunTable } from './dc-drilldown.js';

export const DcAttention = {
  name: 'DcAttention',
  components: { DcRunTable: DcRunTable },
  emits: ['replay'],
  setup: function (props, ctx) {
    var state = dreamcloud.state;

    // "Last updated 14 min ago" has to move while nothing else does.
    var now = Vue.ref(new Date());
    var tick = null;
    Vue.onMounted(function () { tick = setInterval(function () { now.value = new Date(); }, 30000); });
    Vue.onUnmounted(function () { if (tick) clearInterval(tick); });

    var open = Vue.computed(function () {
      return orderProblems(state.problems ? state.problems.open : []);
    });
    var stale = Vue.computed(function () { return staleness(state.lastOkAt, now.value); });

    // The one problem whose rows are showing, and what came back for it.
    var drill = useDrilldown();

    function act(p) {
      var a = problemAction(p);
      if (a.type === 'replay') {
        ctx.emit('replay', { start: a.start, end: a.end });
        return;
      }
      drill.toggle(p.id, a.type, function () {
        if (a.type === 'bookings') return dreamcloud.invoke('online_bookings', a.params);
        if (a.type === 'job') return dreamcloud.invoke('jobs', { job: a.job });
        return dreamcloud.invoke('failures', Object.assign({ limit: 50 }, a.params));
      });
    }

    return {
      state: state,
      open: open,
      stale: stale,
      detail: drill.state,
      act: act,
      label: function (p) { return problemAction(p).label; },
      when: function (iso) { return formatWhen(iso, now.value); }
    };
  },
  template: [
    '<section class="dc-section dc-attention" aria-labelledby="dc-attention-h">',
    '  <h3 class="dc-h" id="dc-attention-h">Needs attention<span v-if="open.length"> ({{ open.length }})</span></h3>',
    '',
    /* Above the list, which stays: an owner reading it should not lose it to a
       refresh that failed. */
    '  <div v-if="state.error" class="dc-banner" role="alert">',
    '    <strong>Couldn&rsquo;t reach DreamCloud.</strong>',
    '    <span>{{ stale || (state.lastOkAt ? "Showing the last figures that loaded." : "Nothing has loaded yet.") }}</span>',
    '    <span class="dc-banner__why">{{ state.error }}</span>',
    '  </div>',
    '',
    '  <p v-if="!state.problems" class="dc-muted">{{ state.error ? "Nothing has loaded yet." : "Loading…" }}</p>',
    '  <p v-else-if="!open.length" class="dc-clear">&#10003; All clear</p>',
    '',
    '  <ul v-else class="dc-alerts">',
    '    <li v-for="p in open" :key="p.id" class="dc-alert"',
    '      :class="p.severity === \'fatal\' ? \'is-fatal\' : \'is-error\'">',
    '      <div class="dc-alert__row">',
    '        <p class="dc-alert__text">',
    '          <strong>{{ p.title }}.</strong> <span v-if="p.detail">{{ p.detail }}</span>',
    '          <span class="dc-alert__since">Since {{ when(p.first_seen) }}</span>',
    '        </p>',
    '        <button type="button" class="btn btn--sm dc-btn" @click="act(p)"',
    '          :aria-expanded="detail.key === p.id ? \'true\' : \'false\'">{{ label(p) }}</button>',
    '      </div>',
    '',
    '      <div v-if="detail.key === p.id" class="dc-rows">',
    '        <p v-if="detail.loading" class="dc-muted">Loading&hellip;</p>',
    '        <p v-else-if="detail.error" class="dc-error">{{ detail.error }}</p>',
    '        <p v-else-if="!detail.rows.length" class="dc-muted">Nothing recorded for this one.</p>',
    '        <table v-else-if="detail.type === \'bookings\'" class="dc-table">',
    '          <thead><tr><th>Booked</th><th>Booking</th><th>Client</th></tr></thead>',
    '          <tbody><tr v-for="b in detail.rows" :key="b.id">',
    '            <td>{{ when(b.created_at) }}</td><td>#{{ b.id }}</td><td>#{{ b.client_id }}</td>',
    '          </tr></tbody>',
    '        </table>',
    '        <dc-run-table v-else-if="detail.type === \'job\'" :runs="detail.rows" />',
    '        <table v-else class="dc-table">',
    '          <thead><tr><th>When</th><th>Where</th><th>Message</th></tr></thead>',
    '          <tbody><tr v-for="f in detail.rows" :key="f.id">',
    '            <td>{{ when(f.occurred_at) }}</td><td>{{ f.area }}</td><td class="dc-table__wrap">{{ f.message }}</td>',
    '          </tr></tbody>',
    '        </table>',
    '      </div>',
    '    </li>',
    '  </ul>',
    '</section>'
  ].join('\n')
};
